import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import PhoneIcon from '@mui/icons-material/PhoneInTalk'
import PlaceIcon from '@mui/icons-material/PlaceOutlined'
import MailIcon from '@mui/icons-material/MailOutline'
import LanguageIcon from '@mui/icons-material/Language'
import type { Customer, StoreMembership } from '@iobiz/shared'
import { QrCode } from '../../ui/QrCode.js'

/**
 * Carte de fidélité imprimable, RECTO ET VERSO.
 *
 * ⭐ Au format d'une carte bancaire — 85,6 × 54 mm. Ce n'est pas une
 * coquetterie : c'est la seule taille qui entre dans un portefeuille, donc la
 * seule qui revienne en boutique. Une carte A5 finit dans un tiroir.
 *
 * ⭐ Deux faces, parce qu'elles ne s'adressent pas aux mêmes yeux. Le RECTO est
 * la carte que le client montre : l'enseigne, et rien d'autre. Le VERSO est
 * pour le comptoir : le titulaire, le code QR qui ouvre sa fiche d'un scan, et
 * les coordonnées de la boutique.
 *
 * ⚠️ Le QR est engendré DANS le navigateur (voir `QrCode`) : envoyer l'identité
 * d'un client à un service d'images distant pour obtenir un dessin serait un
 * partage de données que personne n'a demandé.
 */

/** Largeur et hauteur normalisées d'une carte de paiement (ISO/CEI 7810 ID-1). */
const LARGEUR = '85.6mm'
const HAUTEUR = '54mm'

const NOIR = '#121212'
const BLANC = '#FFFFFF'
/** La feuille du dessous : elle ne se voit qu'en liseré, et suffit au relief. */
const GRIS = '#E8E8E8'
/** Le doré du modèle, en texte : un dégradé ne s'applique pas à des lettres. */
const OR = '#D4AF37'

/**
 * ⭐ Nunito, comme partout ailleurs — et il fallait le dire ICI.
 *
 * La carte héritait d'une pile Helvetica écrite en dur, reprise des documents
 * imprimés où l'on vise une police présente sur toutes les imprimantes. Une
 * carte de fidélité n'est pas un ticket de caisse : elle porte l'identité de la
 * boutique et doit ressembler au reste. La police est auto-hébergée, la CSP
 * étant en `'self'`.
 */
const POLICE = "'Nunito', system-ui, -apple-system, 'Segoe UI', sans-serif"

/**
 * Le dégradé doré.
 *
 * ⚠️ Quatre arrêts, pas deux. L'or n'est pas une couleur mais un REFLET : un
 * dégradé linéaire du clair au foncé donne du beige. Il faut la remontée
 * lumineuse au milieu pour que l'œil y lise du métal.
 *
 * ⚠️ L'identifiant est PARAMÈTRE : deux `<defs>` portant le même `id` dans une
 * page se disputent la référence, et le second dégradé n'est jamais celui qu'on
 * croit.
 */
function DegradeOr({ id }: { id: string }): JSX.Element {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8C6B1F" />
        <stop offset="22%" stopColor="#F6E7A8" />
        <stop offset="48%" stopColor="#C9982A" />
        <stop offset="72%" stopColor="#F3D77E" />
        <stop offset="100%" stopColor="#9A7420" />
      </linearGradient>
    </defs>
  )
}

/**
 * ⚠️ `viewBox` au ratio EXACT de la carte (85,6 / 54 ≈ 100 / 63,1), sinon les
 * courbes se déforment à l'impression alors qu'elles semblaient justes à
 * l'écran.
 */
const VUE = '0 0 100 63.1'

/** Recto : bande blanche au centre, ruban doré à sa gauche, noir de part et d'autre. */
function FondRecto(): JSX.Element {
  return (
    <Box
      component="svg"
      viewBox={VUE}
      preserveAspectRatio="none"
      aria-hidden="true"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <DegradeOr id="or-recto" />
      <rect width="100" height="63.1" fill={NOIR} />
      {/* Ruban doré de gauche : il naît sous le bord haut et gagne le coin
          bas-gauche, exactement comme au modèle. */}
      <path
        d="M12 0 L28 0 C 27 24 20 46 10 63.1 L0 63.1 L0 42 C 6 32 11 18 12 0 Z"
        fill="url(#or-recto)"
      />
      {/* ⭐ DEUX épaisseurs de blanc, décalées. C'est ce léger décrochement —
          un gris pâle qui dépasse à gauche — qui donne l'impression de deux
          feuilles superposées ; une seule forme blanche rend un aplat plat. */}
      <path
        d="M24 0 L90 0 C 88 24 82 46 72 63.1 L8 63.1 C 20 46 25 24 24 0 Z"
        fill={GRIS}
      />
      <path
        d="M31 0 L91 0 C 89 24 83 46 73 63.1 L15 63.1 C 27 46 32 24 31 0 Z"
        fill={BLANC}
      />
      {/* Ruban doré de droite : le noir subsiste au-delà, jusqu'au bord. */}
      <path
        d="M91 0 L98 0 C 96 24 90 46 80 63.1 L73 63.1 C 83 46 89 24 91 0 Z"
        fill="url(#or-recto)"
      />
    </Box>
  )
}

/** Verso : blanc à gauche pour le titulaire, noir à droite pour le comptoir. */
function FondVerso(): JSX.Element {
  return (
    <Box
      component="svg"
      viewBox={VUE}
      preserveAspectRatio="none"
      aria-hidden="true"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <DegradeOr id="or-verso" />
      <rect width="100" height="63.1" fill={BLANC} />
      {/* ⚠️ La dérive vers la gauche est CONTENUE : en descendant plus bas que
          x = 30, le ruban mangeait la moitié de la zone blanche et le nom du
          titulaire finissait écrit sur du doré. */}
      <path
        d="M46 0 L60 0 C 58 26 52 47 42 63.1 L30 63.1 C 40 47 45 26 46 0 Z"
        fill="url(#or-verso)"
      />
      <path d="M60 0 L100 0 L100 63.1 L42 63.1 C 52 47 58 26 60 0 Z" fill={NOIR} />
    </Box>
  )
}

function Carte({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        width: LARGEUR,
        height: HAUTEUR,
        overflow: 'hidden',
        borderRadius: '4mm',
        // ⚠️ Une OMBRE, pas un trait : un filet gris — même fin et pâle —
        // dessine un cadre qui n'existe pas sur une vraie carte plastifiée.
        // Une carte imprimée se découpe SANS bordure ; ce qui la sépare du
        // papier autour, c'est le relief. L'ombre portée simule ce relief et
        // laisse deviner la ligne de découpe sans jamais l'encadrer.
        boxShadow: '0 1mm 3mm rgba(18, 18, 18, 0.35)',
        background: NOIR,
        fontFamily: POLICE,
        flexShrink: 0,
        // ⚠️ Sur une imprimante de bureau, les fonds sont supprimés par défaut.
        // Cette propriété demande de les conserver : sans elle, la carte sort
        // blanche — ni noir, ni or, ni contraste pour lire le texte clair.
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      {children}
    </Box>
  )
}

/** Une ligne de contact au verso : le texte, puis son icône, alignés à droite. */
function Contact({ texte, icone }: { texte: string; icone: JSX.Element }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '1.5mm',
        color: OR,
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '.04em',
      }}
    >
      <span>{texte}</span>
      {icone}
    </Box>
  )
}

export function CarteFidelite({
  open,
  customer,
  boutique,
  onClose,
}: {
  open: boolean
  customer: Customer
  boutique: StoreMembership | undefined
  onClose: () => void
}): JSX.Element {
  const lien = `${window.location.origin}/s/${boutique?.slug ?? ''}/clients/${customer.id}`
  const enseigne = boutique?.legalName || boutique?.name || 'Boutique'
  const iconeOr = { fontSize: '3mm', color: OR }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      {/* ⚠️ Identifiant PROPRE à la carte, distinct du `#ticket` des ventes :
          deux règles d'impression portant le même identifiant finiraient par
          imprimer l'une à la place de l'autre. */}
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#carte-fidelite, #carte-fidelite *': { visibility: 'visible' },
            '#carte-fidelite': { position: 'absolute', left: 0, top: 0 },
          },
        }}
      />

      <DialogTitle>Carte de fidélité</DialogTitle>

      <DialogContent>
        <Box
          id="carte-fidelite"
          sx={{ display: 'flex', gap: 5, flexWrap: 'wrap', p: 2, background: '#fff' }}
        >
          {/* ----------------------------------------------------------- recto */}
          <Carte>
            <FondRecto />

            {/* ⚠️ Centré par `translate(-50%, -50%)`, pas par un `left`/`right`
                approximatif : ce dernier ne centrait le bloc que pour UNE
                largeur de contenu précise, et l'agrandir suffisait à le
                redéporter d'un côté. Le `translate` recentre le bloc sur son
                propre point milieu, quelle que soit sa taille. */}
            <Box
              sx={{
                position: 'absolute',
                left: '56%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '42mm',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2mm',
                textAlign: 'center',
              }}
            >
              {boutique?.logoUrl ? (
                <Box
                  component="img"
                  src={boutique.logoUrl}
                  alt=""
                  /* ⭐ Le logo occupe le HAUT de la carte, pas une vignette au-
                     dessus du nom : c'est lui qu'on reconnaît à un mètre, dans
                     une main tendue au comptoir. Le nom de l'enseigne le
                     confirme, il ne le remplace pas.
                     ⚠️ 22 mm de haut, soit la moitié de la hauteur utile : au-
                     delà, le bloc déborde de la zone blanche par le bas, là où
                     le ruban doré remonte vers la droite. */
                  sx={{ height: '22mm', maxWidth: '40mm', objectFit: 'contain' }}
                />
              ) : (
                // Sans logo, un losange doré aux initiales : le modèle place
                // une marque à cet endroit, et une carte qui commence par du
                // texte nu ne ressemble à aucune carte.
                <Box
                  sx={{
                    // Suit le logo qu'il remplace : un losange resté petit
                    // sous un nom devenu grand se lirait comme une puce de
                    // liste, pas comme une marque.
                    width: '16mm',
                    height: '16mm',
                    transform: 'rotate(45deg)',
                    borderRadius: '2.4mm',
                    border: `0.7mm solid ${OR}`,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      transform: 'rotate(-45deg)',
                      color: OR,
                      fontWeight: 900,
                      fontSize: '17px',
                    }}
                  >
                    {enseigne.slice(0, 2).toUpperCase()}
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  color: NOIR,
                  fontWeight: 900,
                  fontSize: '19px',
                  letterSpacing: '.08em',
                  lineHeight: 1.1,
                }}
              >
                {enseigne.toUpperCase()}
              </Box>
              <Box
                sx={{
                  color: NOIR,
                  fontWeight: 700,
                  fontSize: '8.5px',
                  letterSpacing: '.20em',
                  opacity: 0.75,
                }}
              >
                CARTE DE FIDÉLITÉ
              </Box>
            </Box>
          </Carte>

          {/* ----------------------------------------------------------- verso */}
          <Carte>
            <FondVerso />

            {/* ─── Blanc à gauche : le titulaire.
                ⚠️ La largeur ne peut pas s'étendre librement vers la droite :
                le ruban doré descend EN DIAGONALE, si bien que la place
                disponible se réduit du haut vers le bas — environ 37 mm à
                hauteur du nom, 34 mm à hauteur de la date. La boîte s'arrête
                donc à 37 mm de bord (5 + 32), et c'est le point de départ
                qu'on avance de 6 à 5 mm pour gagner la marge demandée sans
                pousser le texte sous le doré. */}
            <Box
              sx={{
                position: 'absolute',
                left: '5mm',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '32mm',
              }}
            >
              {/* ⚠️ PAS de photo. Elle avait sa logique — vérifier que le
                  porteur est bien le titulaire — mais elle impose au commerçant
                  d'en avoir une pour chaque client, et la plupart des fiches
                  n'en ont pas : la carte sortait alors amputée. Le QR ouvre la
                  fiche, où la photo est, si elle existe. */}
              {/* ⭐ Le nom du titulaire est le TEXTE PRINCIPAL de ce verso,
                  comme sur le modèle. À 10 px il se lisait comme une mention
                  légale au bas d'un document ; c'est pourtant la seule ligne
                  qu'on cherche des yeux quand on rend sa carte à quelqu'un. */}
              <Box
                sx={{
                  color: NOIR,
                  fontWeight: 900,
                  fontSize: '14px',
                  letterSpacing: '.06em',
                  lineHeight: 1.2,
                }}
              >
                {customer.displayName.toUpperCase()}
              </Box>
              <Box sx={{ color: NOIR, fontSize: '10px', fontWeight: 600, mt: '0.8mm' }}>
                {/* ⚠️ Plus la référence : elle est passée sous le QR, et
                    l'imprimer deux fois sur 85 mm ferait douter qu'il s'agisse
                    bien du même numéro. Reste la qualité du porteur. */}
                Client fidèle
              </Box>
              <Box sx={{ width: '20mm', height: '0.5mm', background: OR, mt: '1.8mm' }} />
              {/* ⚠️ La ligne la PLUS BASSE, donc la plus contrainte : le ruban
                  doré remonte vers la droite, il ne laisse ici que ~33,6 mm.
                  Mesurée à 8 px, « Client depuis septembre 2026 » finissait à
                  33,8 mm — elle mordait sur le doré. 7,5 px la ramène à ~32 mm.
                  Toute augmentation future se vérifie à la mesure, pas à l'œil :
                  l'empiètement est de l'ordre du dixième de millimètre. */}
              <Box sx={{ color: NOIR, fontSize: '7.5px', mt: '1.8mm', opacity: 0.7 }}>
                Client depuis{' '}
                {new Date(customer.createdAt).toLocaleDateString('fr-FR', {
                  month: 'long',
                  year: 'numeric',
                })}
              </Box>
            </Box>

            {/* ─── Noir à droite : ce dont le comptoir se sert.
                ⚠️ UNE SEULE colonne flexible, pas deux blocs positionnés
                indépendamment. Le QR et le bloc enseigne+coordonnées avaient
                chacun leur propre `position: absolute` sans se voir l'un
                l'autre : en agrandissant le QR, il a fini par chevaucher
                l'enseigne — c'est exactement ce que `justify-content:
                space-between` sur un seul conteneur empêche, quelle que soit
                la taille du QR ou le nombre de lignes de coordonnées. */}
            <Box
              sx={{
                position: 'absolute',
                top: '4mm',
                bottom: '4mm',
                // ⚠️ 4mm et pas moins : c'est à peu près la marge de sécurité
                // d'usage d'une carte découpée. En deçà, la moindre dérive de
                // coupe — et à la main, il y en a toujours une — entame le
                // texte le plus à droite.
                right: '4mm',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <QrCode
                  valeur={lien}
                  taille={80}
                  titre={`Fiche de ${customer.displayName}`}
                  bordure={false}
                  arrondi={false}
                />
                {/* ⚠️ BLANC, pas doré : ce texte tombait près du ruban, où le
                    jaune sur un fond brun-doré ne se lit presque plus. Le
                    blanc reste net sur les deux fonds de la carte. */}
                {/* ⭐ La RÉFÉRENCE du client sous son propre code QR : c'est
                    le même renseignement, l'un pour la douchette, l'autre pour
                    les doigts. Le jour où le lecteur ne lit pas — écran rayé,
                    pénombre, batterie à plat —, le comptoir retrouve la fiche
                    en tapant ce numéro. Sans lui, une carte qui ne se scanne
                    pas ne sert plus à rien.
                    ⚠️ À DÉFAUT de référence, la consigne d'origine : beaucoup
                    de fiches n'en portent pas, et laisser le vide sous le QR
                    donnerait une carte inachevée. On n'invente pas un numéro
                    pour remplir un blanc. */}
                <Box
                  sx={{
                    color: BLANC,
                    // Une référence se lit chiffre à chiffre pour être recopiée ;
                    // une consigne se lit d'un bloc. D'où deux tailles.
                    fontSize: customer.code ? '8.5px' : '7px',
                    fontWeight: 700,
                    letterSpacing: customer.code ? '.08em' : '.12em',
                    mt: '1mm',
                  }}
                >
                  {customer.code ?? 'SCANNEZ EN CAISSE'}
                </Box>
              </Box>

              {/* ⚠️ Sans l'enseigne : elle est déjà la SEULE chose que porte
                  le recto, et la répéter ici — au-dessus du QR déjà agrandi —
                  encombrait le bloc sans rien ajouter que le comptoir ne
                  sache déjà. Les coordonnées seules suffisent à ce bloc. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.2mm' }}>
                {boutique?.phone && (
                  <Contact texte={boutique.phone} icone={<PhoneIcon sx={iconeOr} />} />
                )}
                {boutique?.email && (
                  <Contact texte={boutique.email} icone={<MailIcon sx={iconeOr} />} />
                )}
                {boutique?.website && (
                  <Contact texte={boutique.website} icone={<LanguageIcon sx={iconeOr} />} />
                )}
                {/* L'adresse EN DERNIER, et seulement à défaut des trois
                    autres : elle est longue, et trois lignes suffisent au bas
                    d'une carte. */}
                {!boutique?.email && !boutique?.website && boutique?.address && (
                  <Contact texte={boutique.address} icone={<PlaceIcon sx={iconeOr} />} />
                )}
              </Box>
            </Box>
          </Carte>
        </Box>

        {!boutique?.phone && !boutique?.email && !boutique?.website && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 3, display: 'block' }}>
            Aucune coordonnée sur la boutique : renseignez téléphone, courriel et site web dans
            ses réglages pour qu’ils figurent au verso.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Fermer</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Imprimer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
